import React, { useState } from 'react'
import { useFragment, graphql } from 'react-relay'
import { Divider, Tabs, Tab } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import Suspense from 'components/Suspense'
import Condition from 'components/Condition'
import { PageLoadingIndicator } from 'components/Layouts/V3'
import { FreelancerProfile_User$key } from '__generated__/FreelancerProfile_User.graphql'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import { Nullable } from 'types'
import { Summary } from './components/Summary'
import UserSkillsFields from './components/fields/UserSkillsFields'
import TimelineEntriesField from './components/fields/TimelineEntriesField'
import VideoAnswersField from './components/fields/VideoAnswersField'
import ProjectSubmissionsField from './components/fields/ProjectSubmissionsField'
import VideoField from './components/fields/VideoField'
import useStyles from './styles'
import ErrorBadge from './components/ErrorBadge'
import BlogPostList from './components/BlogPostList'
import ReferencesTab from './components/ReferencesTab'

export interface IFreelancerProfileProps {
  user: FreelancerProfile_User$key | null
  editable: boolean
  defaultTab?: Nullable<number>
  showTimelineErrorBadge: boolean
  hideVideoField?: boolean
  children?: React.ReactNode
  onVideoRecorderOpen?: () => void
  onVideoRecorderClose?: () => void
}

function FreelancerProfile(props: IFreelancerProfileProps) {
  const {
    user: userFragmentRef,
    editable,
    children,
    showTimelineErrorBadge,
    defaultTab = 0,
    hideVideoField = false,
    onVideoRecorderOpen,
    onVideoRecorderClose,
  } = props

  const { classes } = useStyles()
  const theme = useTheme()

  const isTablet = useResponsiveBreakpoint(theme.breakpoints.values.md)
  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  const [scrollTimeoutHandle, setScrollTimeoutHandle] = useState<number | null>(null)
  const [currentTab, setCurrentTab] = React.useState(defaultTab)
  const tabsWrapperRef = React.useRef<HTMLDivElement | null>(null)

  const user = useFragment(graphql`
    fragment FreelancerProfile_User on User {
      id
      self
      status

      profile {
        openToOpportunities
      }

      ...Summary_UserProfile
      ...TimelineEntriesField_User
      ...UserSkillsFields_User
      ...VideoField_User
      ...VideoAnswersField_User
    }
  `, userFragmentRef)

  const showVideoField = !hideVideoField

  React.useEffect(() => {
    setCurrentTab(defaultTab)

    setScrollTimeoutHandle(window.setTimeout(() => {
      if (tabsWrapperRef.current && defaultTab) {
        tabsWrapperRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    }, 0))

    return () => {
      if (scrollTimeoutHandle) {
        window.clearTimeout(scrollTimeoutHandle)
      }
    }
  }, [defaultTab])

  if (!user) return null

  const liteMode = !user.profile?.openToOpportunities

  return (
    <div className={classes.freelancer} data-cy="freelancer">
      <div className={classes.container}>
        <div>
          <div>
            <Summary
              user={user}
              editable={editable}
            />

            <Condition condition={!liteMode}>
              <UserSkillsFields
                editable={editable}
                user={user}
              />

              <div className={classes.actions}>
                {children}
              </div>

              <Condition condition={showVideoField}>
                <Suspense fallback={<PageLoadingIndicator />}>
                  <VideoField
                    user={user}
                    editable={editable}
                    onOpen={onVideoRecorderOpen}
                    onClose={onVideoRecorderClose}
                  />
                </Suspense>
              </Condition>

              <Condition condition={isTablet}>
                <Divider />
              </Condition>

              <div ref={tabsWrapperRef}>
                <Tabs
                  className={classes.tabs}
                  value={currentTab}
                  onChange={(e, newTab) => setCurrentTab(newTab)}
                  indicatorColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab
                    label={(
                      <React.Fragment>
                        <Condition condition={!isMobile}>
                          Work & Education

                          <Condition condition={currentTab !== 0}>
                            <ErrorBadge count={showTimelineErrorBadge ? 1 : 0} />
                          </Condition>
                        </Condition>

                        <Condition condition={isMobile}>
                          Timeline <ErrorBadge count={showTimelineErrorBadge ? 1 : 0} />
                        </Condition>
                      </React.Fragment>
                    )}
                    className={classes.tab}
                    data-cy="tab-work-education"
                    data-testid="tab-work-education"
                  />

                  {user?.status !== 'pending' && (
                    <Tab
                      label={(
                        <React.Fragment>
                          <Condition condition={!isMobile}>
                            Video Answers
                          </Condition>

                          <Condition condition={isMobile}>
                            Answers
                          </Condition>
                        </React.Fragment>
                      )}
                      className={classes.tab}
                      data-cy="tab-video-answers"
                      data-testid="tab-video-answers"
                    />
                  )}

                  {user?.status !== 'pending' && (
                    <Tab
                      label={(
                        <React.Fragment>
                          <Condition condition={!isMobile}>
                            Sample Work
                          </Condition>

                          <Condition condition={isMobile}>
                            Samples
                          </Condition>
                        </React.Fragment>
                      )}
                      className={classes.tab}
                      data-cy="tab-sample-work"
                      data-testid="tab-sample-work"
                    />
                  )}

                  {user?.status !== 'pending' && (
                    <Tab
                      label="References"
                      className={classes.tab}
                      data-cy="tab-references"
                      data-testid="tab-references"
                    />
                  )}

                  {user?.status !== 'pending' && (
                    <Tab
                      label="Posts"
                      className={classes.tab}
                      data-cy="tab-posts"
                      data-testid="tab-posts"
                    />
                  )}
                </Tabs>
              </div>

              {user?.id && (
                <div style={{ display: currentTab === 0 ? 'block' : 'none' }}>
                  {/* "Work & Education" tab */}
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <TimelineEntriesField userId={user.id} editable={editable} />
                  </Suspense>
                </div>
              )}

              {currentTab === 1 && user?.id && (
                // "Video Answers" tab
                <Suspense fallback={<PageLoadingIndicator />}>
                  <VideoAnswersField
                    editable={editable}
                    userId={user.id}
                  />
                </Suspense>
              )}

              {currentTab === 2 && user?.id && (
                // "Sample Work" tab
                // TODO: only show this tab if sample work submissions are enabled for freelancer type
                (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <ProjectSubmissionsField editable={editable} userId={user.id} />
                  </Suspense>
                )
              )}

              {currentTab === 3 && user?.id && (
                // "References" tab
                (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <ReferencesTab editable={editable} userId={user.id} />
                  </Suspense>
                )
              )}

              {currentTab === 4 && user?.id && (
                // "Posts" tab
                (
                  <Suspense fallback={<PageLoadingIndicator />}>
                    <BlogPostList
                      userId={user.id}
                      editable={editable}
                    />
                  </Suspense>
                )
              )}
            </Condition>
          </div>
        </div>
      </div>
    </div>
  )
}
export default React.memo(FreelancerProfile)
